import type { TranslationShape } from '../types';

export const de = {
  common: {
    loading: 'Lädt...',
    initializing: 'Initialisierung...',
    continue: 'Weiter',
    back: 'Zurück',
    retry: 'Erneut versuchen',
    tryAgain: 'Erneut versuchen',
    goBack: 'Zurück',
    cancel: 'Abbrechen',
    close: 'Schließen',
    closeModal: 'Modal schließen',
    closeWindow: 'Fenster schließen',
    startOver: 'Neu starten',
    switchToMobile: 'Auf Mobilgerät wechseln',
    enableCamera: 'Kamera aktivieren',
    returnToSite: 'Zur Website zurück',
    notice: 'Hinweis',
    done: 'Fertig',
    saving: 'Wird gespeichert...',
    processing: 'Verarbeitung...',
    error: 'Fehler',
    moreInfo: 'Weitere Informationen',
    productFallback: 'Verifizierung',
  },
  ageEstimateUnavailable: {
    title: 'Wir konnten dein Alter gerade nicht prüfen',
    description:
      'Bei der Prüfung deines Selfies ist bei uns ein Fehler aufgetreten. Du kannst es erneut versuchen oder dich mit deinem Ausweis und dem gerade aufgenommenen Selfie verifizieren.',
    descriptionIdOnly:
      'Bei der Prüfung deines Selfies ist bei uns ein Fehler aufgetreten. Du kannst mit deinem Ausweis und dem gerade aufgenommenen Selfie fortfahren.',
    useId: 'Mit Ausweis verifizieren',
  },
  pipelineRecovery: {
    title: 'Die Verifizierung wurde vorübergehend unterbrochen',
    description:
      'Dieser Schritt konnte gerade nicht fortgesetzt werden. Deine Verifizierung bleibt aktiv.',
    retrying: 'Wir versuchen es automatisch erneut. Bitte lasse diese Seite geöffnet.',
    readyToRetry: 'Versuche es erneut, wenn du bereit bist.',
    retryingAction: 'Erneuter Versuch…',
  },
  lifecycleRecovery: {
    loadTitle: 'Der nächste Schritt konnte nicht geladen werden', loadDescription: 'Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    retryTitle: 'Versuchen wir es noch einmal', retryDescription: 'Dieser Schritt wurde unterbrochen. Bitte versuchen Sie es erneut.', retrySelfieTitle: 'Machen wir Ihr Selfie noch einmal', retrySelfieDescription: 'Schauen Sie bei gutem Licht in die Kamera und versuchen Sie es erneut.', retryIdTitle: 'Machen wir noch ein Foto Ihres Ausweises', retryIdDescription: 'Achten Sie darauf, dass Ihr ganzer Ausweis zu sehen und gut lesbar ist.',
    restartStepTitle: 'Versuchen wir diesen Schritt noch einmal', restartStepDescription: 'Wir müssen diesen Schritt neu beginnen, um fortzufahren.',
    startAgain: 'Erneut starten', beginAgain: 'Neu beginnen', cannotContinueTitle: 'Diese Verifizierung kann nicht fortgesetzt werden',
    cannotContinueDescription: 'Beginnen Sie mit einer neuen Verifizierung.', waitingTitle: 'Wir schließen diesen Schritt ab…',
    waitingDescription: 'Bitte lassen Sie diese Seite geöffnet. Wir fahren automatisch fort.', cancelledTitle: 'Verifizierung abgebrochen',
    completeTitle: 'Verifizierung abgeschlossen', closeDescription: 'Sie können dieses Fenster schließen.', returnDescription: 'Sie können zur Website zurückkehren.',
    continueTitle: 'Bereit zum Fortfahren', continueDescription: 'Fahren Sie mit dem nächsten Schritt fort.',
    rateLimitedTitle: 'Wir benötigen einen Moment', rateLimitedDescription: 'Der nächste Schritt kann gerade nicht geprüft werden. Ihre Verifizierung bleibt aktiv.', checkStatus: 'Status prüfen',
    finalizingTitle: 'Ihre Verifizierung wird abgeschlossen…',
    finalizingDescription: 'Bitte lassen Sie diese Seite geöffnet. Das dauert meist nur wenige Sekunden.',
    unconfirmedTitle: 'Wir bestätigen Ihr Ergebnis noch',
    unconfirmedDescription: 'Das dauert länger als üblich. Sie müssen keine Schritte wiederholen.',
    checkAgain: 'Erneut prüfen',
  },
  footer: {
    terms: 'Nutzungsbedingungen',
    privacy: 'Datenschutz',
    biometric: 'Biometrie',
    languageLabel: 'Sprache',
    copyright: '© {{year}} {{companyName}}. Alle Rechte vorbehalten.',
  },
  mainScreen: {
    title: 'Anonym verifizieren',
    description:
      'Schnelle, private Altersverifizierung. Wir erfassen oder teilen keine Details.',
    iconAlt: 'Sichere Verifizierung',
    primaryAction: 'Jetzt verifizieren',
    secondaryAction: 'Mit Emblem anmelden',
    separatorText: 'Schon verifiziert?',
    missingSession:
      'Sitzungskonfiguration fehlt. Bitte starten Sie die Verifizierung von Anfang an.',
    directAccessBlocked:
      'Direkter Zugriff ist nicht erlaubt. Bitte starten Sie die Verifizierung von Anfang an.',
    emblemResumeNotice:
      "Emblem sign-in wasn't completed. You can continue with verification here.",
    emblemNoReusableNotice:
      "You don't have a reusable Emblem pass for this site yet. You can continue with verification here.",
  },
  emblemStepUp: {
    title: 'One more verification step',
    description:
      'Your Emblem passkey worked, but this site requires a higher level of verification.',
    primaryAction: 'Continue verification',
    iconAlt: 'Emblem verification step up',
    detailTitle: 'What happens next',
    detailPoint1: 'Complete a quick SafePassage verification for this site.',
    detailPoint2:
      'After you finish, your Emblem passkey will be upgraded for future L2 sites.',
    privacyHint: 'You are still verifying privately. Sites only receive the result.',
  },
  loading: {
    title: 'Sicheres Verifizierungssystem wird eingerichtet',
    description: 'Ihre persönlichen Daten werden niemals erfasst, gespeichert oder geteilt',
    status: {
      initializing: 'Wird initialisiert...',
      settingUpApiKey: 'API-Schlüssel wird eingerichtet...',
      ready: 'Bereit zur Verifizierung',
      errorRetrying: 'Fehler beim Initialisieren – wird erneut versucht...',
    },
    errors: {
      noApiKey: 'Kein API-Schlüssel konfiguriert. Bitte einen API-Schlüssel angeben.',
    },
  },
  cameraAccess: {
    title: 'Kamerazugriff erforderlich',
    description: 'Kamerazugriff ist erforderlich, um Ihr Alter zu verifizieren und fortzufahren',
    continueDesktop: 'Auf dem Desktop fortfahren',
    separatorText: 'ODER',
    noWebcam: 'Keine Webcam?',
    privacyNotice: 'Ihr Bild wird sofort verarbeitet und umgehend gelöscht',
    qr: {
      title: 'Auf dem Handy fortfahren',
      description: 'Scannen Sie diesen Code, um die Verifizierung auf dem Handy abzuschließen',
      imageAlt: 'QR-Code für mobile Verifizierung',
      instruction: 'Richten Sie die Kamera Ihres Handys auf diesen Code',
      waiting: 'Warten auf Verbindung...',
      generating: 'QR-Code wird generiert...',
      errorMissingSession: 'Sitzungs-ID fehlt. Bitte gehen Sie zurück und versuchen Sie es erneut.',
      errorUnavailable: 'QR-Code konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
      errorFailed: 'QR-Code konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    },
  },
  cameraSetup: {
    defaultMessage: 'Positionieren Sie Ihr Gesicht im Kreis',
    warmup: {
      preparing: 'Ihre Verifizierung wird vorbereitet...',
      preparingDelayed:
        'Die Verbindung zum Verifizierungsdienst dauert länger als erwartet. Bitte lassen Sie diese Seite geöffnet.',
      requesting: 'Warten auf Kameraberechtigung...',
      initializing: 'Kamera wird gestartet...',
      positioning: 'Kamerabild wird vorbereitet...',
      delayed:
        'Das dauert länger als erwartet. Bitte lassen Sie diese Seite geöffnet, während Ihre Kamera startet.',
    },
    analysis: {
      title: 'Ihr Foto wird verarbeitet',
      description: 'Das dauert in der Regel ein paar Sekunden.',
      privacy: 'Bilder werden sicher verarbeitet und nicht gespeichert.',
      failureMessage: 'Die Prüfung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
    },
    errors: {
      stalledTitle: 'Kamera wurde nicht gestartet',
      interruptedTitle: 'Kamera wurde unterbrochen',
      missingSessionId: 'Sitzungs-ID fehlt im Verifizierungsergebnis.',
      faceTimeout:
        'Gesichtserkennung zeitüberschritten. Bitte stellen Sie sicher, dass Ihr Gesicht in der Kamera gut sichtbar ist.',
      faceTimeoutDetail:
        'Es wurde innerhalb der vorgegebenen Zeit kein Gesicht erkannt. Bitte versuchen Sie es erneut und achten Sie darauf, dass Ihr Gesicht deutlich im Kreis sichtbar ist.',
    },
  },
  cameraFeedback: {
    initial_position: 'Zentrieren Sie Ihr Gesicht im Bild',
    initial_lighting: 'Sorgen Sie für gutes Licht',
    initial_stability: 'Halten Sie die Kamera ruhig',
    wide_zone_position: 'Zentrieren Sie Ihr Gesicht im Bild',
    wide_zone_center: 'Zentrieren Sie Ihr Gesicht im Bild',
    wide_too_close: 'Gehen Sie ein Stück zurück',
    wide_off_center: 'Zentrieren Sie Ihr Gesicht im Bild',
    wide_movement: 'Bleiben Sie still',
    wide_gaze: 'Schauen Sie geradeaus',
    wide_success: 'Gute Position!',
    zone_transition: 'Perfekt! Kommen Sie jetzt etwas näher',
    great_now_move_closer: 'Perfekt! Kommen Sie jetzt etwas näher',
    close_zone_position: 'Perfekt! Genau so bleiben',
    close_zone_gaze: 'Schauen Sie geradeaus',
    close_too_far: 'Kommen Sie etwas näher',
    close_too_close: 'Gehen Sie ein Stück zurück',
    close_off_center: 'Zentrieren Sie Ihr Gesicht im Bild',
    close_movement: 'Bleiben Sie still',
    close_gaze: 'Schauen Sie geradeaus',
    close_success: 'Perfekt! Genau so bleiben',
    processing: 'Fertig! Verarbeitung läuft...',
    no_face_detected: 'Kein Gesicht erkannt',
    move_closer: 'Kommen Sie etwas näher',
    move_further: 'Gehen Sie ein Stück zurück',
    center_face: 'Zentrieren Sie Ihr Gesicht im Bild',
    look_at_camera: 'Schauen Sie geradeaus',
    improve_lighting: 'Lassen Sie uns das noch einmal versuchen',
    good_position: 'Sieht gut aus!',
    move_closer_to_camera: 'Kommen Sie etwas näher',
    move_further_from_camera: 'Gehen Sie ein Stück zurück',
    good_wide_position: 'Gute Position!',
    good_close_position: 'Perfekt! Genau so bleiben',
  },
  cameraProcessing: {
    verifying: 'Gut gemacht! Ihre Verifizierung wird verarbeitet',
    analyzing: 'Wird analysiert...',
  },
  idRequest: {
    l2: {
      title: 'Ausweisverifizierung erforderlich',
      description: 'Bitte legen Sie einen amtlichen Ausweis vor, um die Verifizierung abzuschließen',
    },
    l1: {
      failed: {
        title: 'Unser Scan konnte Ihr Alter nicht bestätigen',
        description: 'Wir benötigen einen kurzen Ausweischeck, um fortzufahren',
      },
      unavailable: {
        title: 'Zum Abschluss ein kurzer Ausweischeck',
        description:
          'Wir konnten dein Alter diesmal nicht anhand deines Selfies prüfen und verwenden stattdessen deinen Ausweis.',
      },
      additional: {
        title: 'Zusätzliche Verifizierung erforderlich',
        description: 'Zur Einhaltung der Vorschriften müssen wir Ihr Alter mit einem Ausweis überprüfen',
      },
    },
    primaryAction: 'Weiter',
    privacyNotice: 'Wir speichern oder teilen Ihre persönlichen Daten nicht',
    privacyTooltip:
      'Ihr Ausweis wird nur zur Altersverifizierung verwendet. Er wird niemals gespeichert, aufbewahrt oder an die anfragende Website weitergegeben. Die anfragende Website erhält lediglich eine einfache Bestätigung, dass Sie die Altersanforderung erfüllen.',
    iconAlt: 'Ausweiskarte',
  },
  idCapture: {
    errorTitle: 'Fotoaufnahme fehlgeschlagen',
    initializingTitle: 'Kamera wird initialisiert',
    initializingDescription: 'Dokumentenaufnahme wird vorbereitet...',
    title: 'Machen Sie ein Foto der Vorderseite Ihres Ausweises',
    recaptureTitle: 'Dieses Ausweisfoto konnten wir nicht akzeptieren',
    instructions:
      'Stellen Sie sicher, dass Ihr Ausweis vollständig sichtbar ist, der Text klar lesbar ist und alle Ränder im Bild sind',
    recaptureInstructions:
      'Achten Sie darauf, dass Ihr Ausweis klar und vollständig sichtbar ist, und nehmen Sie das Foto erneut auf.',
    capturing: 'Aufnahme...',
    recaptureAction: 'Foto erneut aufnehmen',
    captureAction: 'Ausweis scannen',
    capturedTitle: 'Foto aufgenommen!',
    capturedDescription: 'Für die Prüfung wird vorbereitet...',
    cameraErrorTitle: 'Kamerafehler',
    cameraErrorAlt: 'Kamerafehler',
    cameraRecoveryDescription:
      'Versuchen Sie es erneut, wechseln Sie die Kamera, falls verfügbar, oder prüfen Sie, ob dieser Browser die Kamera verwenden darf.',
    cameraPermissionHelp:
      'Prüfen Sie auf iPhone oder iPad den Kamerazugriff für Safari in den Einstellungen. Verwenden Sie in Chrome oder Safari das Kamerasymbol in der Adressleiste, um den Zugriff zu erlauben.',
    switchCameraAction: 'Kamera wechseln',
  },
  idReview: {
    title: 'Überprüfen Sie Ihren gescannten Ausweis',
    description: 'Stellen Sie sicher, dass alle Informationen gut sichtbar sind',
    imageAlt: 'Aufgenommener Ausweis',
    guidance: 'Ihr Ausweis sollte sichtbar und lesbar sein.',
    missingImage: 'Kein Bild aufgenommen. Bitte gehen Sie zurück und fotografieren Sie Ihren Ausweis.',
    submitAction: 'Weiter',
    retakeAction: 'Wiederholen',
  },
  idProcessing: {
    title: 'Ausweis wird geprüft',
    description:
      'Dieser Vorgang dauert in der Regel etwa 20-30 Sekunden. In einigen Fällen kann er bis zu 60 Sekunden dauern.',
    keepOpen: 'Bitte lassen Sie diese Seite geöffnet, bis die Verifizierung abgeschlossen ist.',
    info: 'Ihr Dokument wird sicher verarbeitet. Es werden keine persönlichen Informationen gespeichert.',
    status: {
      initial: 'Verifizierung läuft.',
      stillWorking: 'Ihr Dokument wird noch geprüft...',
      almostDone: 'Fast geschafft. Vielen Dank für Ihre Geduld.',
    },
    steps: {
      received: 'Dokument empfangen',
      analyzing: 'Informationen werden analysiert',
      verifying: 'Echtheit wird überprüft',
      confirming: 'Alter wird bestätigt',
    },
    errors: {
      noDocumentData: 'Keine Dokumentdaten gefunden. Bitte fotografieren Sie Ihren Ausweis erneut.',
      noSessionId: 'Keine Sitzungs-ID verfügbar. Bitte starten Sie die Verifizierung neu.',
      selfieFetchFailed:
        'Selfie konnte nicht vom Server abgerufen werden. Bitte starten Sie die Verifizierung neu.',
      noApiKey: 'API-Schlüssel nicht verfügbar. Bitte aktualisieren Sie die Seite und starten Sie neu.',
      generic: 'Verifizierung fehlgeschlagen.',
    },
  },
  desktopWaiting: {
    title: 'Gerät verbunden',
    description: 'Schließen Sie die Verifizierung auf Ihrem Mobilgerät ab',
    iconAlt: 'Mobiles Gerät',
    keepOpen: 'Dieses Fenster geöffnet lassen',
    redirectInfoLine1: 'Sie werden automatisch weitergeleitet, wenn',
    redirectInfoLine2: 'die Verifizierung abgeschlossen ist.',
    expiredTitle: 'Sitzung abgelaufen',
    expiredDescription: 'Ihre Verifizierungssitzung ist abgelaufen. Bitte starten Sie neu.',
  },
  returnToDesktop: {
    title: 'Zurück zum Desktop',
    description:
      'Mobile Verifizierung abgeschlossen. Kehren Sie zu Ihrem Desktop-Browser zurück, um fortzufahren.',
    iconAlt: 'Desktop-Computer',
  },
  recovery: {
    title: 'Verifizierung fortsetzen?',
    description: 'Wir haben eine laufende Verifizierungssitzung gefunden',
    lastSeen: 'Zuletzt gesehen: {{screen}}',
    progressLine: '{{age}} • {{progress}}',
    progress: {
      start: 'Gerade gestartet',
      early: 'Früher im Prozess',
      good: 'Guter Fortschritt',
      halfway: 'Mehr als halb geschafft',
      almost: 'Fast fertig',
      nearlyComplete: 'Beinahe abgeschlossen',
    },
    time: {
      minuteAgo: 'vor {{count}} Minute',
      minutesAgo: 'vor {{count}} Minuten',
      secondAgo: 'vor {{count}} Sekunde',
      secondsAgo: 'vor {{count}} Sekunden',
    },
    screens: {
      main: 'Willkommen',
      emblemStepUp: 'Emblem Step Up',
      cameraSetup: 'Kameraeinrichtung',
      verification: 'Altersverifizierung',
      idRequest: 'Ausweis erforderlich',
      idCapture: 'Dokumentaufnahme',
      idProcessing: 'Dokumentverarbeitung',
      idReview: 'Aufnahme prüfen',
      success: 'Verifizierung abgeschlossen',
      failure: 'Verifizierung fehlgeschlagen',
      error: 'Technischer Fehler',
      unknown: '{{screenName}}',
    },
    actions: {
      continue: 'Fortsetzen, wo ich aufgehört habe',
      startFresh: 'Neu starten',
    },
    capabilities: {
      connection: '✓ Verbindung kann wiederhergestellt werden',
      camera: '✓ Kameraeinstellungen gespeichert',
      manual: 'Einige Einstellungen müssen möglicherweise neu konfiguriert werden',
    },
    info: {
      continuingLabel: 'Fortsetzen:',
      continuingDescription: 'Verifizierung an der letzten Stelle fortsetzen.',
      startFreshLabel: 'Neu starten:',
      startFreshDescription: 'Eine neue Verifizierungssitzung beginnen.',
    },
  },
  errorScreen: {
    helpTitle: 'Was Sie tun können:',
    invalidSession: {
      title: 'Ungültige Sitzung',
      description:
        'Die Verifizierungssitzung ist ungültig oder abgelaufen. Bitte starten Sie den Verifizierungsprozess erneut.',
      primaryAction: 'Support kontaktieren',
      suggestion1: 'Zur Website des Händlers zurückkehren',
      suggestion2: 'Verifizierungsprozess erneut starten',
      suggestion3: 'Support kontaktieren, wenn das Problem bestehen bleibt',
    },
    expiredSession: {
      title: 'Sitzung abgelaufen',
      description:
        'Ihre Verifizierungssitzung ist abgelaufen. Kehren Sie zur Website oder App zurück, auf der Sie begonnen haben.',
      primaryAction: 'Zur Website zurückkehren',
      suggestion1: 'Kehren Sie zur ursprünglichen Website oder App zurück',
      suggestion2: 'Starten Sie dort eine neue Verifizierung',
      suggestion3: 'Wenden Sie sich bei Bedarf an den Anbieter',
    },
    network: {
      title: 'Verbindungsproblem',
      description:
        'Es konnte keine Verbindung zu den Verifizierungsdiensten hergestellt werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
      suggestion1: 'Prüfen Sie Ihre Internetverbindung',
      suggestion2: 'Deaktivieren Sie ein VPN, falls Sie eines nutzen',
      suggestion3: 'Versuchen Sie, die Seite neu zu laden',
      suggestion4: 'Kontaktieren Sie den Support, wenn das Problem weiterhin besteht',
    },
    unsupported: {
      title: 'Browser nicht unterstützt',
      description:
        'Ihr Browser unterstützt die erforderlichen Funktionen für die Altersverifizierung nicht. Bitte verwenden Sie einen modernen Browser wie Chrome, Firefox oder Safari.',
      primaryAction: 'Chrome herunterladen',
      suggestion1: 'Aktualisieren Sie Ihren aktuellen Browser',
      suggestion2: 'Versuchen Sie Chrome, Firefox oder Safari',
      suggestion3: 'Aktivieren Sie JavaScript, falls deaktiviert',
    },
    cameraDenied: {
      title: 'Kamerazugriff erforderlich',
      description:
        'Für die Altersverifizierung ist Kamerazugriff erforderlich. Bitte erlauben Sie den Zugriff und versuchen Sie es erneut.',
      suggestion1: 'Klicken Sie auf das Kamerasymbol in der Adressleiste',
      suggestion2: 'Wählen Sie "Zulassen" für den Kamerazugriff',
      suggestion3: 'Aktualisieren Sie die Seite nach dem Erteilen der Berechtigung',
    },
    missingReturnUrl: {
      title: 'Konfigurationsfehler',
      description:
        'Der Verifizierungssitzung fehlen erforderliche Konfigurationen. Bitte kontaktieren Sie den Händler, um die Integration zu korrigieren.',
      primaryAction: 'Händler kontaktieren',
      suggestion1: 'Zur Website des Händlers zurückkehren',
      suggestion2: 'Kontaktieren Sie das Support-Team des Händlers',
      suggestion3: 'Melden Sie, dass die {{productName}}-Integration aktualisiert werden muss',
      suggestion4:
        'Der Händler muss eine gültige Rückgabe-URL in der SDK-Konfiguration bereitstellen',
    },
    directAccess: {
      title: 'Direkter Zugriff nicht erlaubt',
      description:
        'Die Altersverifizierung muss über die Website eines Händlers mit dem {{productName}} SDK gestartet werden. Sie können diesen Dienst nicht direkt aufrufen.',
      primaryAction: 'Seite schließen',
      iconAlt: 'Zugriff blockiert',
      suggestion1: 'Gehen Sie zurück zur Händler-Website, auf der Sie begonnen haben',
      suggestion2:
        'Schließen Sie die Altersverifizierung über den Checkout- oder Registrierungsprozess ab',
      suggestion3: 'Diese Verifizierungs-URL nicht speichern oder teilen',
      suggestion4:
        'Jede Verifizierungssitzung ist eindeutig und muss vom Händler gestartet werden',
    },
    subscriptionRequired: {
      title: 'Abonnement erforderlich',
      description:
        'Dieses Konto benötigt ein aktives Abonnement für Verifizierungen in der Produktion. Kontaktieren Sie Ihren Administrator, um den Plan zu aktualisieren.',
      primaryAction: 'Administrator kontaktieren',
      portalAction: 'Abrechnungsportal öffnen',
    },
    planLimitReached: {
      title: 'Monatliches Limit erreicht',
      description:
        'Dieses Konto hat das monatliche Verifizierungslimit erreicht. Kontaktieren Sie Ihren Administrator, um den Plan zu aktualisieren, oder warten Sie bis zur nächsten Abrechnungsperiode.',
      primaryAction: 'Administrator kontaktieren',
      portalAction: 'Abrechnungsportal öffnen',
    },
    sandboxLimitReached: {
      title: 'Sandbox-Limit erreicht',
      description:
        'Dieses Konto hat das Sandbox-Verifizierungslimit erreicht. Bitten Sie einen Administrator, den Sandbox-Modus zu deaktivieren oder den Plan zu aktualisieren.',
      primaryAction: 'Administrator kontaktieren',
      portalAction: 'Abrechnungsportal öffnen',
    },
  },
  failure: {
    genericError: 'Verifizierung fehlgeschlagen',
    iconAlt: 'Verifizierung fehlgeschlagen',
    document: {
      title: 'Verifizierung fehlgeschlagen',
      description: 'Wir konnten die Verifizierung nicht abschließen. Versuchen wir es erneut.',
      reason1: 'Probleme mit der Bildqualität des Dokuments',
      reason2: 'Informationen sind nicht klar sichtbar',
      reason3: 'Dokumenttyp wird nicht unterstützt',
      reason4: 'Anforderungen zur Altersverifizierung nicht erfüllt',
    },
    face: {
      title: 'Verifizierung fehlgeschlagen',
      description: 'Wir konnten die Verifizierung nicht abschließen. Versuchen wir es erneut.',
      reason1: 'Lebenderkennung nicht bestanden',
      reason2: 'Schlechte Lichtverhältnisse',
      reason3: 'Kamerapositionierung problematisch',
      reason4: 'Bewegung während der Aufnahme',
      reason5: 'Gesicht nicht klar sichtbar',
    },
    crossDeviceRestart: {
      title: 'Zurück zum Desktop',
      description:
        'Diese Verifizierung muss im Desktop-Browser neu gestartet werden, der den QR-Code angezeigt hat. Kehren Sie dorthin zurück, klicken Sie auf Erneut versuchen und scannen Sie dann den neuen QR-Code mit Ihrem Telefon.',
    },
    errors: {
      restartFailed: 'Neustart der Verifizierung fehlgeschlagen. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
      switchFailed: 'Wechsel zum Mobilgerät fehlgeschlagen. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
    },
  },
  success: {
    title: 'Altersverifizierung erfolgreich',
    emblemTitle: 'Emblem-Konto erstellt!',
    emblemDescription:
      'Ihre portable Altersverifizierung ist jetzt bereit. Sie können Ihr Emblem-Konto verwenden, um Ihr Alter auf jeder Website zu bestätigen, die Emblem unterstützt.',
    iconAlt: 'Erfolg',
    continueAction: 'Zur Website weiter',
    separatorText: 'zukünftige Verifizierungen überspringen',
    createEmblemAction: 'Emblem-Konto erstellen',
    emblemEnrollmentComplete: 'Emblem-Konto erstellt',
    readyDescription: 'Sie können jetzt auf Ihre Inhalte zugreifen',
    redirectingIn: 'Weiterleitung in {{seconds}} Sekunden...',
    returnUrlInvalid:
      'Die Rückgabe-URL ist nicht konfiguriert oder ungültig. Bitte klicken Sie auf Weiter, um fortzufahren.',
    errors: {
      missingReturnUrl: 'Fehlende Rückgabe-URL – SDK-Fehlkonfiguration des Händlers',
      emblemSignupFailed: 'Emblem-Konto konnte nicht erstellt werden',
    },
  },
  emblem: {
    info: {
      ariaLabel: 'Mehr über Emblem erfahren',
      trigger: 'Was ist Emblem?',
      title: 'Was ist Emblem?',
      paragraph1:
        'Emblem speichert Ihre Altersverifizierung sicher, sodass Sie 18+ nachweisen können, ohne persönliche Daten zu teilen.',
      paragraph2:
        'Nach der Verifizierung über {{productName}} können Sie ein Emblem-Konto erstellen, um künftige Verifizierungen auf teilnehmenden Websites zu überspringen. Ihre Verifizierung bleibt privat; Websites erfahren nur, dass Sie verifiziert sind.',
      linkLabel: 'Emblem-Website',
    },
    signupInfo: {
      ariaLabel: 'Mehr über die Erstellung eines Emblem-Kontos erfahren',
      trigger: 'Warum ein Konto erstellen?',
      title: 'Emblem-Konto erstellen',
      intro: 'Speichern Sie Ihre Verifizierung für die Zukunft! Mit einem Emblem-Konto können Sie:',
      bullet1: 'Zukünftige Altersverifizierungen auf teilnehmenden Websites überspringen',
      bullet2: 'Ihre persönlichen Informationen privat halten',
      bullet3: 'Altersbeschränkte Inhalte sofort aufrufen',
      outro:
        'Ihre Verifizierung wird sicher gespeichert. Websites erfahren nur, dass Sie verifiziert sind.',
      linkLabel: 'Emblem-Website',
    },
  },
  mobileCompletion: {
    title: 'Verifizierung abgeschlossen',
    description:
      'Mobile Verifizierung erfolgreich abgeschlossen. Sie können dieses Fenster schließen und zu Ihrem Desktop zurückkehren.',
    iconAlt: 'Desktop-Computer',
    closeWindowFallback:
      'Bitte schließen Sie dieses Tab/Fenster manuell, um zu Ihrem Desktop zurückzukehren.',
  },
  cameraErrorBoundary: {
    iconAlt: 'Kamerafehler',
    trySteps: 'Versuchen Sie diese Schritte:',
    skipCamera: 'Ohne Kamera fortfahren',
    permission: {
      title: 'Kamerazugriff erforderlich',
      description: 'Für die Altersverifizierung benötigen wir Zugriff auf Ihre Kamera.',
      step1: 'Klicken Sie auf das Kamerasymbol in der Adressleiste',
      step2: 'Wählen Sie "Zulassen" für den Kamerazugriff',
      step3: 'Klicken Sie unten auf "Erneut versuchen"',
    },
    notFound: {
      title: 'Keine Kamera erkannt',
      description: 'Wir konnten keine Kamera auf Ihrem Gerät finden.',
      step1: 'Prüfen Sie, ob Ihre Kamera korrekt verbunden ist',
      step2: 'Versuchen Sie einen anderen Browser',
      step3: 'Verwenden Sie ein Gerät mit integrierter Kamera',
    },
    inUse: {
      title: 'Kamera in Verwendung',
      description: 'Ihre Kamera wird von einer anderen Anwendung verwendet.',
      step1: 'Schließen Sie andere Apps, die die Kamera verwenden könnten',
      step2: 'Prüfen Sie Videoanrufe oder Aufnahme-Software',
      step3: 'Starten Sie den Browser neu und versuchen Sie es erneut',
    },
    unsupported: {
      title: 'Browser nicht unterstützt',
      description: 'Ihr Browser unterstützt den Kamerazugriff nicht.',
      step1: 'Aktualisieren Sie Ihren Browser auf die neueste Version',
      step2: 'Verwenden Sie Chrome, Firefox, Safari oder Edge',
      step3: 'Aktivieren Sie WebRTC in den Browser-Einstellungen',
    },
    default: {
      title: 'Kamerafehler',
      description: 'Beim Zugriff auf Ihre Kamera ist ein Problem aufgetreten.',
      step1: 'Prüfen Sie die Kameraverbindung',
      step2: 'Laden Sie die Seite neu und versuchen Sie es erneut',
      step3: 'Versuchen Sie einen anderen Browser',
    },
    platform: {
      chrome: 'Chrome/Edge:',
      chromeInstruction: 'Klicken Sie auf das Kamerasymbol in der Adressleiste',
      firefox: 'Firefox:',
      firefoxInstruction: 'Klicken Sie auf das Berechtigungssymbol neben der URL',
      safari: 'Safari:',
      safariInstruction: 'Gehen Sie zu Safari -> Einstellungen -> Websites -> Kamera',
    },
  },
  verificationErrorBoundary: {
    iconAlt: 'Fehler',
    retryAttempt: 'Wiederholungsversuch {{attempt}} von {{max}}',
    errorDetails: 'Fehlerdetails',
    errorType: 'Fehlertyp:',
    errorTypeGeneral: 'allgemein',
    componentStack: 'Komponenten-Stack:',
    cameraDenied: {
      title: 'Kamerazugriff verweigert',
      description:
        'Bitte erlauben Sie den Kamerazugriff in Ihren Browser-Einstellungen, um fortzufahren.',
    },
    noCamera: {
      title: 'Keine Kamera gefunden',
      description: 'Bitte stellen Sie sicher, dass eine funktionierende Kamera angeschlossen ist.',
    },
    cameraError: {
      title: 'Kamerafehler',
      description:
        'Es gab ein Problem mit Ihrer Kamera. Bitte prüfen Sie die Kamera und versuchen Sie es erneut.',
    },
    documentFailed: {
      title: 'Dokumentaufnahme fehlgeschlagen',
      description:
        'Wir konnten Ihr Dokument nicht erfassen. Bitte sorgen Sie für gutes Licht und versuchen Sie es erneut.',
    },
    connectionLost: {
      title: 'Verbindung verloren',
      description:
        'Die Verbindung zu unseren Servern wurde unterbrochen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
    },
    multipleErrors: {
      title: 'Mehrere Fehler sind aufgetreten',
      description:
        'Es gibt wiederkehrende Probleme. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.',
    },
    default: {
      title: 'Etwas ist schiefgelaufen',
      description:
        'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut oder starten Sie neu, wenn das Problem weiterhin besteht.',
    },
  },
  errorState: {
    title: 'Etwas ist schiefgelaufen',
  },
  notifications: {
    errorTitle: 'Fehler',
    successTitle: 'Erfolg',
    infoTitle: 'Info',
    cameraAccessDenied:
      'Der Kamerazugriff wurde verweigert. Bitte erlauben Sie den Zugriff und versuchen Sie es erneut.',
    noCameraFound: 'Keine Kamera gefunden. Bitte stellen Sie sicher, dass Ihr Gerät eine Kamera hat.',
    cameraInUse: 'Die Kamera wird bereits von einer anderen Anwendung verwendet.',
    networkError: 'Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
  },
  cameraErrors: {
    notAllowed:
      'Wir benötigen Kamerazugriff, um fortzufahren. Wählen Sie "Erneut versuchen", um ihn anzufordern. Wenn Ihr Browser nicht erneut fragt, erlauben Sie den Kamerazugriff für diese Website in den Browsereinstellungen und versuchen Sie es dann erneut oder laden Sie die Seite neu.',
    notAllowedAction: 'Bitte klicken Sie auf Zulassen, wenn die Kameraberechtigung angefordert wird',
    security:
      'Der Kamerazugriff wird durch die Sicherheitseinstellungen Ihres Browsers oder Geräts blockiert. Erlauben Sie dort den Kamerazugriff für diese Website und versuchen Sie es erneut oder laden Sie die Seite neu.',
    securityAction:
      'Erlauben Sie den Kamerazugriff in den Sicherheitseinstellungen Ihres Browsers oder Geräts',
    notFound: 'Keine Kamera gefunden. Bitte stellen Sie sicher, dass eine Kamera verbunden ist.',
    notFoundAction: 'Bitte schließen Sie eine Kamera an',
    notReadable: 'Die Kamera wird bereits von einer anderen Anwendung verwendet.',
    notReadableAction: 'Bitte schließen Sie andere Anwendungen, die die Kamera verwenden',
    overconstrained: 'Kameraeinstellungen werden nicht unterstützt. Standardwerte werden verwendet...',
    overconstrainedAction: 'Es wird mit Basis-Kameraeinstellungen erneut versucht',
    timeout: 'Zeitüberschreitung bei der Kamerainitialisierung. Bitte versuchen Sie es erneut.',
    timeoutAction: 'Bitte versuchen Sie es erneut',
    videoStalled:
      'Ihre Kamera wurde eingeschaltet, aber es kam kein Videobild an. Wählen Sie "Erneut versuchen", um die Kamera neu zu starten. Wenn das Problem weiterhin besteht, schließen Sie andere Apps, die die Kamera verwenden, oder laden Sie die Seite neu.',
    videoStalledAction: 'Bitte versuchen Sie es erneut, um die Kamera neu zu starten',
    interrupted:
      'Ihre Kamera wurde unerwartet beendet, möglicherweise durch einen Anruf oder eine andere App. Wählen Sie "Erneut versuchen", um die Kamera neu zu starten.',
    interruptedAction: 'Bitte versuchen Sie es erneut, um die Kamera neu zu starten',
    initializationFailed: 'Kamerainitialisierung fehlgeschlagen. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
    streamUnavailable: 'Kamerastream nicht verfügbar. Bitte prüfen Sie die Kameraverbindung.',
    generic: 'Kamerazugriff fehlgeschlagen. Bitte prüfen Sie die Kamera und versuchen Sie es erneut.',
    genericAction: 'Bitte aktualisieren Sie die Seite und versuchen Sie es erneut',
  },
  brandHeader: {
    tagline: 'Altersverifizierung',
    merchantLabel: 'Verifizierung für',
    secureLabel: 'Sicher',
  },
  faceMatchRecovery: {
    selfieRetry: {
      title: 'Versuchen wir es mit einem neuen Selfie',
      message:
        'Wir konnten Ihr Selfie dieses Mal nicht Ihrem Ausweisfoto zuordnen. Ausweisfotos können Jahre alt sein, und Veränderungen im Aussehen oder bei der Beleuchtung können es schwieriger machen, Sie zu erkennen.',
      tipsHeading: 'Diese Tipps helfen bei Ihrem nächsten Selfie:',
      tips: {
        straightOn: 'Schauen Sie direkt in die Kamera und halten Sie Ihr ganzes Gesicht im Bild.',
        lighting: 'Suchen Sie einen hellen Ort, an dem das Licht von vorne auf Sie fällt.',
        expression:
          'Versuchen Sie, denselben Gesichtsausdruck wie auf Ihrem Ausweisfoto zu zeigen. Wenn Sie dort nicht lächeln, wählen Sie einen entspannten, neutralen Ausdruck.',
        eyes: 'Achten Sie darauf, dass Ihre Augen gut zu sehen sind, ohne Spiegelungen oder Sonnenbrille.',
      },
      primaryAction: 'Neues Selfie aufnehmen',
      captureHeading: 'Für Ihr neues Selfie',
      captureTips: {
        straightOn: 'Direkt in die Kamera schauen',
        lighting: 'Licht von vorne',
        expression: 'Gleicher Ausdruck wie auf dem Ausweisfoto',
        eyes: 'Augen gut sichtbar, keine Sonnenbrille',
      },
      comparing: 'Ihr neues Selfie wird mit Ihrem Ausweis verglichen…',
    },
    repeated: {
      title: 'Immer noch Probleme? Versuchen wir es noch einmal.',
      message:
        'Ein älteres Ausweisfoto oder andere Lichtverhältnisse können es schwieriger machen. Schauen Sie bei gutem Licht in die Kamera und versuchen Sie denselben Gesichtsausdruck wie auf Ihrem Ausweisfoto.',
      primaryAction: 'Weiteres Selfie aufnehmen',
    },
    help: {
      heading: 'Brauchen Sie Hilfe?',
      differentIdText:
        'Wenn Sie einen anderen Lichtbildausweis mit einem aktuelleren Foto von Ihnen haben, können Sie stattdessen diesen verwenden.',
      differentIdAction: 'Anderen Ausweis verwenden',
    },
    differentId: {
      captureTitle: 'Fotografieren Sie Ihren anderen Ausweis',
      captureInstructions:
        'Fotografieren Sie die Vorderseite des anderen Ausweises. Achten Sie darauf, dass er gut lesbar und vollständig sichtbar ist.',
    },
    retainedIdUnavailable:
      'Wir benötigen ein neues Foto Ihres Ausweises, um fortzufahren. Achten Sie darauf, dass er gut lesbar und vollständig sichtbar ist.',
  },
} as const satisfies TranslationShape;
